import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineChevronLeft from 'Components/Atoms/OutlineIcons/OutlineChevronLeft';
import OutlineChevronRight from 'Components/Atoms/OutlineIcons/OutlineChevronRight';
import { cn } from 'Utils/cn';
import { useLocation } from 'react-router-dom';

const SCROLL_DISTANCE = 140;

const HorizontalScroll = forwardRef(function HorizontalScroll({ children, className }, ref) {
    const scrollRef = useRef(null);
    const [hasScrollLeft, setHasScrollLeft] = useState(false);
    const [hasScrollRight, setHasScrollRight] = useState(false);
    const contentRef = useRef(null);
    const { pathname } = useLocation();

    useEffect(() => {
        requestAnimationFrame(() => {
            updateScrollButtons();
        });
    }, [pathname]);

    useImperativeHandle(
        ref,
        () => ({
            update: updateScrollButtons,
        }),
        [],
    );

    const updateScrollButtons = () => {
        const el = scrollRef.current;
        if (!el) return;
        setHasScrollLeft(Math.round(el.scrollLeft) > 0);
        setHasScrollRight(Math.round(el.scrollLeft) + el.clientWidth < el.scrollWidth - 2);
    };

    useEffect(() => {
        updateScrollButtons();
        const delayUpdateScrollButtons = () => {
            setTimeout(() => {
                updateScrollButtons();
            }, 100);
        };

        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateScrollButtons);
        window.addEventListener('app-layout-update-scroll-header', delayUpdateScrollButtons);
        window.addEventListener('resize', updateScrollButtons);
        return () => {
            el.removeEventListener('scroll', updateScrollButtons);
            window.removeEventListener('app-layout-update-scroll-header', delayUpdateScrollButtons);
            window.removeEventListener('resize', updateScrollButtons);
        };
    }, []);

    const scrollBy = (dir) => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollBy({
            left: dir * SCROLL_DISTANCE,
            behavior: 'smooth',
        });
    };

    return (
        <div className={cn('relative h-full w-full', className)}>
            {hasScrollLeft && (
                <div className="absolute left-0 w-12 flex items-center justify-center top-0 bottom-0 bg-gradient-to-r from-white to-transparent z-20 pointer-events-none">
                    <IconButton
                        icon={<OutlineChevronLeft />}
                        size="small"
                        className="rounded-full pointer-events-auto"
                        onClick={() => scrollBy(-1)}
                    />
                </div>
            )}
            <div
                ref={scrollRef}
                className="overflow-auto flex gap-2 items-center scrollbar-none absolute inset-0 scroll-smooth justify-between"
            >
                <div className="h-full grow flex-shrink-0 flex items-center" ref={contentRef}>
                    {children}
                </div>
            </div>
            {hasScrollRight && (
                <div className="absolute right-0 w-12 flex items-center justify-center top-0 bottom-0 bg-gradient-to-l from-white to-transparent z-20 pointer-events-none">
                    <IconButton
                        icon={<OutlineChevronRight />}
                        size="small"
                        className="rounded-full pointer-events-auto"
                        onClick={() => scrollBy(1)}
                    />
                </div>
            )}
        </div>
    );
});

export default HorizontalScroll;

HorizontalScroll.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
};
